import React, { useEffect, useMemo, useRef, useState } from "react";

export function clientToWorld(clientX, clientY, rect, view) {
  const scale = Math.min(rect.width / view.w, rect.height / view.h);
  const left = rect.left + (rect.width - view.w * scale) / 2;
  const top = rect.top + (rect.height - view.h * scale) / 2;
  return [(clientX - left) / scale + view.x, -((clientY - top) / scale + view.y)];
}

const initialView = (scene) => {
  const w = Math.max(1, scene.max[0] - scene.min[0]), h = Math.max(1, scene.max[1] - scene.min[1]);
  const padding = Math.max(w, h) * 0.02;
  return { x: scene.min[0] - padding, y: -scene.max[1] - padding, w: w + padding * 2, h: h + padding * 2 };
};

function DxfNavigator({ scene, view, drawing, onChange, onReset }) {
  const full = useMemo(() => initialView(scene), [scene]);
  const dragging = useRef(null);
  useEffect(() => { dragging.current = null; }, [scene]);
  const move = (event) => {
    const [x, y] = clientToWorld(event.clientX, event.clientY, event.currentTarget.getBoundingClientRect(), full);
    onChange((current) => ({ ...current, x: x - current.w / 2, y: -y - current.h / 2 }));
  };
  return <div className="bg-white border rounded shadow-sm p-1" style={{ position: "absolute", top: 8, right: 8, width: 168 }}>
    <div className="d-flex align-items-center justify-content-between px-1">
      <small className="text-muted">Navigátor</small>
      <button type="button" className="btn btn-sm btn-light py-0" aria-label="Navigátor: teljes rajz" title="Teljes rajz" onClick={onReset}>⌂</button>
    </div>
    <svg role="img" aria-label="DXF navigátor: kattints vagy húzd a nézet mozgatásához" tabIndex={0}
      viewBox={`${full.x} ${full.y} ${full.w} ${full.h}`}
      style={{ display: "block", width: "100%", height: 110, background: "#f1f5f9", cursor: "move", touchAction: "none" }}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        dragging.current = event.pointerId;
        move(event);
      }}
      onPointerMove={(event) => { if (dragging.current === event.pointerId) move(event); }}
      onPointerUp={(event) => {
        if (dragging.current === event.pointerId) {
          dragging.current = null;
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
      }}
      onPointerCancel={() => { dragging.current = null; }}
      onLostPointerCapture={() => { dragging.current = null; }}
      onKeyDown={(event) => {
        const delta = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[event.key];
        if (delta) {
          event.preventDefault();
          onChange((v) => ({ ...v, x: v.x + delta[0] * v.w / 4, y: v.y + delta[1] * v.h / 4 }));
        } else if (event.key === "Home") { event.preventDefault(); onReset(); }
      }}>
      <g stroke="#64748b" strokeWidth={Math.max(full.w, full.h) / 500} fill="none">{drawing}</g>
      <rect x={view.x} y={view.y} width={view.w} height={view.h}
        fill="#0d6efd" fillOpacity="0.15" stroke="#0d6efd" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
    <small className="text-muted d-block px-1" style={{ fontSize: 10 }}>Kattints / húzd · nyílbillentyűk</small>
  </div>;
}

const validRect = (rect) => Array.isArray(rect) && rect.length === 4 && rect.every(Number.isFinite) && rect[0] < rect[2] && rect[1] < rect[3];
const rectInside = (outer, inner) => inner[0] >= outer[0] && inner[1] >= outer[1] && inner[2] <= outer[2] && inner[3] <= outer[3];

export const validRegions = (regions) => regions.length > 0 && regions.length <= 30 &&
  regions.every((r) => validRect(r.rect) && r.name.trim() && (r.cutouts || []).every((cutout) => validRect(cutout.rect) && rectInside(r.rect, cutout.rect))) &&
  new Set(regions.map((r) => r.name.trim().toLocaleLowerCase())).size === regions.length;

export default function DxfRegionSelector({ scene, regions, onChange, disabled }) {
  const [view, setView] = useState(() => initialView(scene));
  const [mode, setMode] = useState("pan");
  const [cutoutTarget, setCutoutTarget] = useState(null);
  const [draft, setDraft] = useState(null);
  const drag = useRef(null);
  const drawing = useMemo(() => scene.lines.map(([x1, y1, x2, y2], i) => <line key={i} x1={x1} y1={-y1} x2={x2} y2={-y2} />), [scene.lines]);

  useEffect(() => {
    setView(initialView(scene));
    setMode("pan");
    setCutoutTarget(null);
    setDraft(null);
    drag.current = null;
  }, [scene]);

  useEffect(() => {
    if (cutoutTarget && !regions.some((r) => r.id === cutoutTarget)) {
      setCutoutTarget(null);
      setMode("pan");
    }
  }, [cutoutTarget, regions]);

  const zoom = (factor) => setView((v) => ({ x: v.x + v.w * (1 - factor) / 2, y: v.y + v.h * (1 - factor) / 2, w: v.w * factor, h: v.h * factor }));
  const rectangle = (rect, label, key, color, dashed = false) => <g key={key}>
    <rect x={rect[0]} y={-rect[3]} width={Math.max(0, rect[2] - rect[0])} height={Math.max(0, rect[3] - rect[1])}
      fill={`${color}22`} stroke={color} strokeDasharray={dashed ? `${view.w / 150} ${view.w / 220}` : undefined} strokeWidth={view.w / 600} />
    <text x={rect[0]} y={-rect[3] - view.h / 150} fill={color} fontSize={view.w / 65}>{label}</text>
  </g>;

  const beginMode = (nextMode, target = null) => {
    setMode(nextMode);
    setCutoutTarget(target);
    setDraft(null);
    drag.current = null;
  };

  const finishSelection = (rect) => {
    if (mode === "select") {
      if (regions.length >= 30) return;
      onChange([...regions, { id: crypto.randomUUID(), name: `Rész-${regions.length + 1}`, rect, cutouts: [] }]);
      return;
    }
    if (mode === "cutout" && cutoutTarget) {
      const target = regions.find((r) => r.id === cutoutTarget);
      if (!target || !rectInside(target.rect, rect)) return;
      onChange(regions.map((r) => r.id === cutoutTarget
        ? { ...r, cutouts: [...(r.cutouts || []), { id: crypto.randomUUID(), rect }] }
        : r));
    }
  };

  const activeCutoutRegion = regions.find((r) => r.id === cutoutTarget);

  return <section className="border rounded p-3 my-3">
    <h3 className="h5">Kézi DXF-kijelölés</h3>
    <p>Húzz külső keretet az ajtó vagy rész köré, majd nevezd el. Egy részhez ezután egy vagy több kivágási keretet is rajzolhatsz, így például keretes elem készíthető.</p>
    <p className="text-muted">A külső keret a megtartandó terület, a narancssárga szaggatott keretek a kivágások. A kivágásnak teljesen a saját külső keretén belül kell lennie.</p>
    <div className="d-flex flex-wrap gap-2 mb-2">
      <button className={`btn btn-sm btn-${mode === "select" ? "primary" : "outline-primary"}`} disabled={disabled} onClick={() => beginMode("select")}>Külső keret rajzolása</button>
      <button className={`btn btn-sm btn-${mode === "pan" ? "primary" : "outline-primary"}`} disabled={disabled} onClick={() => beginMode("pan")}>Nézet mozgatása</button>
      <button className="btn btn-sm btn-outline-secondary" onClick={() => zoom(0.7)}>Nagyítás</button>
      <button className="btn btn-sm btn-outline-secondary" onClick={() => zoom(1 / 0.7)}>Kicsinyítés</button>
      <button className="btn btn-sm btn-outline-secondary" onClick={() => setView(initialView(scene))}>Teljes rajz</button>
    </div>
    {mode === "cutout" && activeCutoutRegion && <p role="status" className="text-warning mb-2">
      Kivágás rajzolása ehhez: <strong>{activeCutoutRegion.name}</strong>. A keretet a külső kijelölésen belül húzd meg.
    </p>}
    <div style={{ position: "relative" }}>
    <svg role="img" aria-label="DXF kézi kijelölés" viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
      style={{ width: "100%", height: 520, background: "#f1f5f9", touchAction: "none", cursor: mode === "pan" ? "grab" : "crosshair" }}
      onPointerDown={(e) => {
        if (disabled || e.button !== 0 || (mode === "select" && regions.length >= 30)) return;
        if (mode === "cutout" && !cutoutTarget) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        drag.current = { point: clientToWorld(e.clientX, e.clientY, e.currentTarget.getBoundingClientRect(), view), view, x: e.clientX, y: e.clientY };
      }} onPointerMove={(e) => {
        if (!drag.current || disabled) return;
        const [x, y] = clientToWorld(e.clientX, e.clientY, e.currentTarget.getBoundingClientRect(), drag.current.view);
        const [sx, sy] = drag.current.point;
        if (mode === "pan") setView({ ...drag.current.view, x: drag.current.view.x + sx - x, y: drag.current.view.y + y - sy });
        else setDraft([Math.min(sx, x), Math.min(sy, y), Math.max(sx, x), Math.max(sy, y)]);
      }} onPointerUp={(e) => {
        if (drag.current && mode !== "pan" && !disabled && Math.hypot(e.clientX - drag.current.x, e.clientY - drag.current.y) > 3) {
          const [x, y] = clientToWorld(e.clientX, e.clientY, e.currentTarget.getBoundingClientRect(), drag.current.view);
          const [sx, sy] = drag.current.point;
          if (x !== sx && y !== sy) finishSelection([Math.min(sx, x), Math.min(sy, y), Math.max(sx, x), Math.max(sy, y)]);
        }
        drag.current = null;
        setDraft(null);
      }} onPointerCancel={() => { drag.current = null; setDraft(null); }}>
      <g stroke="#64748b" strokeWidth={view.w / 1400} fill="none">
        {drawing}
      </g>
      {regions.filter((r) => validRect(r.rect)).map((r) => rectangle(r.rect, r.name, r.id, "#0891b2"))}
      {regions.flatMap((r) => (r.cutouts || []).filter((c) => validRect(c.rect)).map((cutout, i) => rectangle(cutout.rect, `${r.name} kivágás ${i + 1}`, `${r.id}-${cutout.id}`, "#ea580c", true)))}
      {draft && rectangle(draft, mode === "cutout" ? "Új kivágás" : "Új rész", "draft", mode === "cutout" ? "#ea580c" : "#e11d48", mode === "cutout")}
    </svg>
    <DxfNavigator scene={scene} view={view} drawing={drawing} onChange={setView} onReset={() => setView(initialView(scene))} />
    </div>
    <p className="text-muted">Az előnézet ritkított. A koordináták a forrás mértékegységében értendők. Legfeljebb 30 kimeneti rész jelölhető ki.</p>
    {regions.map((region, index) => <fieldset disabled={disabled} key={region.id} className="border rounded p-2 mb-2">
      <div className="d-flex flex-wrap gap-2 align-items-end">
        <label>Rész neve<input className="form-control" aria-label={`${index + 1}. rész neve`} maxLength={150} value={region.name} onChange={(e) => onChange(regions.map((r) => r.id === region.id ? { ...r, name: e.target.value } : r))} /></label>
        {["X minimum", "Y minimum", "X maximum", "Y maximum"].map((label, i) => <label key={label}>{label}<input className="form-control" style={{ width: 140 }} type="number" step="any" aria-label={`${index + 1}. ${label}`} value={Number.isFinite(region.rect[i]) ? Number(region.rect[i].toFixed(4)) : ""} onChange={(e) => onChange(regions.map((r) => r.id === region.id ? { ...r, rect: r.rect.map((v, j) => j === i ? e.target.valueAsNumber : v) } : r))} /></label>)}
        <button type="button" className={`btn ${mode === "cutout" && cutoutTarget === region.id ? "btn-warning" : "btn-outline-warning"}`} onClick={() => beginMode("cutout", region.id)}>{mode === "cutout" && cutoutTarget === region.id ? "Kivágás rajzolása…" : "Kivágás hozzáadása"}</button>
        <button type="button" className="btn btn-outline-danger" onClick={() => onChange(regions.filter((r) => r.id !== region.id))}>Keret törlése</button>
      </div>
      {(region.cutouts || []).map((cutout, cutoutIndex) => <div key={cutout.id} className="d-flex flex-wrap gap-2 align-items-end mt-2 ms-3 border-start border-warning border-3 ps-2">
        <strong className="align-self-center">Kivágás {cutoutIndex + 1}</strong>
        {["X minimum", "Y minimum", "X maximum", "Y maximum"].map((label, i) => <label key={label}>{label}<input className="form-control" style={{ width: 140 }} type="number" step="any" aria-label={`${index + 1}. rész ${cutoutIndex + 1}. kivágás ${label}`} value={Number.isFinite(cutout.rect[i]) ? Number(cutout.rect[i].toFixed(4)) : ""} onChange={(e) => onChange(regions.map((r) => r.id === region.id ? { ...r, cutouts: (r.cutouts || []).map((c) => c.id === cutout.id ? { ...c, rect: c.rect.map((v, j) => j === i ? e.target.valueAsNumber : v) } : c) } : r))} /></label>)}
        <button type="button" className="btn btn-outline-danger" aria-label={`${index + 1}. rész ${cutoutIndex + 1}. kivágás törlése`} onClick={() => onChange(regions.map((r) => r.id === region.id ? { ...r, cutouts: (r.cutouts || []).filter((c) => c.id !== cutout.id) } : r))}>Kivágás törlése</button>
      </div>)}
    </fieldset>)}
    {regions.length > 0 && !validRegions(regions) && <p role="alert" className="text-danger">A nevek legyenek különbözők, minden keretnek legyen pozitív mérete, és a kivágások maradjanak a saját külső keretükön belül.</p>}
  </section>;
}
